import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { isToday, parseISO } from 'date-fns';
import { onboardingTasks } from '@ourbranch/lookups';
import { Grid } from '@material-ui/core';

import useSession from 'core/hooks/use-session';
import { Card } from 'core/components/card';
import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { ApplicationLinks } from './components/application-url-form';
import { Task } from './components/task';
import completedIcon from 'customer/assets/completed.svg';
import clock from 'core/assets/svg/clock-filled.svg';
import coffeeIcon from './assets/coffee.svg';
import { InventoryImages } from './components/inventory-images';
import useStyles from './onboarding-task-center.styles';

const tasksThatCanBeUploadedByExternalAgent = [
  onboardingTasks.NeedProofOfPriorCoverage,
  onboardingTasks.ProvideProofOfConnectedHome,
  onboardingTasks.ConnectedHomeRenewal,
  onboardingTasks.NeedScheduledPersonalPropertyImages,
  onboardingTasks.ConfirmWindstormMitigationStatus,
  onboardingTasks.NeedProofThatAccidentPreventionCourseTaken,
  onboardingTasks.ProvideProofOfGoodStudent
];

const OnboardingTaskCenter = () => {
  const classes = useStyles();
  const { canEditPolicies, canUploadTaskCenterDocuments, isAgency } = useSession();
  const {
    account: { accountLoading, fname, lname, tasks, incompleteTasks, completedTasks, tasksError, created }
  } = useStore();
  const toast = useToast();

  useEffect(() => {
    if (tasksError) {
      toast.notify({
        type: 'error',
        message: tasksError
      });
    }
  }, [tasksError, toast]);

  const boundToday = created ? isToday(parseISO(created)) : false;

  // Appcues teaching bubble for task statuses (#24215)
  useEffect(() => {
    // Only show for IAs (not agencies) and when there are review pending tasks
    if (!isAgency && completedTasks.length > 0) {
      const hasReviewPendingTasks = completedTasks.some((task) => task.completedDate && !task.approvalDate);

      if (hasReviewPendingTasks) {
        // Show the teaching bubble with low priority (after other bubbles)
        setTimeout(() => {
          window?.Appcues?.show('24215');
        }, 1000); // Small delay to ensure other bubbles show first
      }
    }
  }, [isAgency, completedTasks]);

  return (
    <Section
      title="Task Center"
      rightLabel="Total"
      rightValue={tasks.length ? `${accountLoading ? '...' : incompleteTasks.length} incomplete tasks` : null}
    >
      {accountLoading ? (
        <Loading />
      ) : (
        <div className={classes.sectionContent}>
          {canEditPolicies && <ApplicationLinks />}
          <InventoryImages />

          {!tasks.length && boundToday && (
            <Card className={classes.container}>
              <Grid container justifyContent="center" alignItems="center" direction="column">
                <img className={classes.icon} src={clock} alt="Clock Icon" />
                <Label type="darkGreenMedium" className={classes.label}>
                  Onboarding tasks are generating
                </Label>
                <Label type="greenExtraSmall">Try refreshing the page to see if they're ready.</Label>
              </Grid>
            </Card>
          )}

          {!tasks.length && !boundToday && (
            <Card>
              <div className={classes.empty}>
                <img className={classes.icon} src={boundToday ? clock : coffeeIcon} alt="Icon" />
                <Label className={classes.greenLabel}>
                  {boundToday
                    ? 'Onboarding tasks generating, please check back soon.'
                    : 'Relax, there are no tasks to complete'}
                </Label>
              </div>
            </Card>
          )}
          {tasks.length > 0 && (
            <Card style={{ overflow: 'visible' }}>
              {incompleteTasks.length ? (
                incompleteTasks.map((task, ix) => {
                  const isTaskUploadEnabled =
                    canUploadTaskCenterDocuments &&
                    (isAgency ? tasksThatCanBeUploadedByExternalAgent.includes(task.taskName) : true);

                  return <Task key={ix} {...task} readOnly={!isTaskUploadEnabled} />;
                })
              ) : (
                <div className={classes.container}>
                  <img src={completedIcon} alt="Rounded checkmark" />
                  <Label type="darkGreenSmall">
                    All of {fname} {lname}’s tasks have been completed
                  </Label>
                </div>
              )}
              <CollapsibleCard
                showChevron={Boolean(completedTasks.length)}
                className={classes.collapsible}
                initialOpen={true}
                content={
                  <>
                    <Label type="infoSubtitle" className={classes.greenLabel}>
                      Submitted
                    </Label>
                    <Label type="noteSmall" className={classes.greenLabel}>
                      {completedTasks.length} {completedTasks.length === 1 ? 'task' : 'tasks'} submitted
                    </Label>
                  </>
                }
                type="noBorders"
              >
                {completedTasks.map((task, ix) => (
                  <Task key={ix} {...task} />
                ))}
              </CollapsibleCard>
            </Card>
          )}
        </div>
      )}
    </Section>
  );
};

export default observer(OnboardingTaskCenter);
